<template>
  <div class="broadcast-editor">
    <vue-editor
      v-model="html"
      :editor-toolbar="toolbar"
      :editor-options="editorOptions"
      :placeholder="placeholder"
      @ready="listenToUser"
    />

    <div class="broadcast-editor__footer">
      <span class="broadcast-editor__counter" :class="{ 'is-over': remaining < 0 }">
        {{ remaining }} caracteres restantes
      </span>
    </div>

    <p class="broadcast-editor__hint">
      Cor, tamanho, fonte, alinhamento e sublinhado não existem em Markdown e por
      isso ficam fora da barra.
    </p>
  </div>
</template>

<script>
/**
 * Editor do corpo da campanha.
 *
 * A EDICAO e WYSIWYG (Quill, via vue2-editor, que ja era dependencia do
 * projeto), mas o VALOR que sai daqui e MARKDOWN — e o que a API grava e o que
 * os quatro clientes renderizam. O V2 usa react-markdown sem rehype-raw; o
 * legado e o preview do Master usam markdown-it com `html:false`. Todos
 * mostrariam HTML cru como TEXTO, entao gravar o HTML do Quill quebraria a
 * exibicao em todo lugar.
 *
 * A barra so expoe o que Markdown representa. Cor, tamanho, fonte, alinhamento
 * e sublinhado ficaram de fora de proposito: o Quill os produz, o Markdown nao
 * os carrega, e uma barra que promete formatacao que some na gravacao e pior
 * que uma barra menor.
 *
 * Imagem nao entra no corpo por caminho nenhum: sem botao na barra, fora da
 * lista de formatos do Quill, barrada ao colar e ao arrastar, e descartada na
 * conversao para Markdown. O Quill embute a imagem como base64 no corpo, o que
 * inflaria o payload replicado para os 8 shards. O campo de imagem saiu do
 * formulario; `imageUrl` continua no contrato, indo sempre nulo.
 */
import { VueEditor } from 'vue2-editor';
import { BROADCAST_BODY_MAX } from './broadcastEnums';
import { htmlToMarkdown } from './broadcastHtmlToMarkdown';
import { renderBroadcastMarkdown } from './broadcastMarkdown';

/**
 * HTML para SEMEAR o Quill. O markdown-it poe "\n" entre as tags de bloco
 * ("<ul>\n<li>...</li>\n</ul>\n"), e o Quill 1.3, recebendo isso por
 * innerHTML, desmonta a lista: cada "\n" solto vira paragrafo e os itens somem.
 * So a quebra ENTRE tags sai; espaco dentro do texto fica como esta.
 */
const toEditorHtml = (markdown) => renderBroadcastMarkdown(markdown).replace(/>\n+</g, '><').trim();

/**
 * Colar passa pelo clipboard do Quill: um matcher de IMG devolve delta vazio.
 * Arrastar uma imagem (arquivo ou de outra pagina) entra direto no DOM, sem
 * passar pelo clipboard, entao o drop e cancelado antes.
 */
const blockImages = (quill) => {
  const Delta = quill.constructor.import('delta');
  quill.clipboard.addMatcher('IMG', () => new Delta());

  quill.root.addEventListener('drop', (event) => {
    const data = event.dataTransfer;
    if (!data) return;
    const hasFile = data.files && data.files.length > 0;
    const hasImage = /<img/i.test(data.getData('text/html') || '');
    if (hasFile || hasImage) event.preventDefault();
  }, true);
};

export default {
  name: 'broadcastEditor',
  components: { VueEditor },
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: 'Escreva a mensagem da campanha' },
  },
  data() {
    return {
      // HTML que o Quill edita. O v-model do pai continua sendo Markdown.
      html: '',
      // Ultimo Markdown que ESTE componente emitiu. Sem isto o ciclo
      // html -> markdown -> prop -> html re-semearia o Quill a cada tecla e o
      // cursor saltaria para o fim do texto.
      lastEmitted: this.value || '',
      // HTML que a propria semeadura escreveu. O watcher de `html` ignora esse
      // valor: carregar um texto nao e editar, e o Markdown que volta do Quill
      // nao pode substituir o que veio gravado (a volta Markdown -> Quill ->
      // Markdown nao e garantidamente identica). So a digitacao emite.
      seededHtml: null,
      seededMarkdown: '',
      quill: null,
      lastRange: null,
      // So o que a barra oferece: o Quill descarta qualquer outro formato
      // (imagem, video, cor...) mesmo quando ele chega colado.
      editorOptions: {
        formats: ['bold', 'italic', 'header', 'list', 'link'],
      },
      toolbar: [
        ['bold', 'italic'],
        [{ header: [false, 1, 2, 3] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
    };
  },
  computed: {
    remaining() {
      return BROADCAST_BODY_MAX - (this.value || '').length;
    },
  },
  methods: {
    /**
     * Avisa o pai quando quem mexeu no texto foi a PESSOA: digitar, colar,
     * arrastar, usar a barra ou inserir um marcador pelo chip. Semear o texto
     * (ao abrir e quando o valor muda por fora) passa pela API do Quill em modo
     * 'silent' e nao conta: e o que deixa a Liberacao continuar escrevendo o
     * texto gerado ate o operador editar.
     */
    listenToUser(quill) {
      this.quill = quill;
      blockImages(quill);
      // Semeia pela API do Quill, nao por innerHTML: o innerHTML passa pelo
      // MutationObserver, que o Quill reporta como fonte 'user' — e a Liberacao
      // achava que o operador tinha editado o texto gerado e parava de segui-lo.
      this.seed(this.value);
      quill.on('text-change', (delta, oldContents, source) => {
        if (source === 'user') this.$emit('user-edit');
      });
      // Guarda o ultimo cursor: o clique no chip de marcador chega depois do
      // editor perder a selecao.
      quill.on('selection-change', (range) => {
        if (!range) return;
        this.lastRange = range;
        this.$emit('focus');
      });
    },
    seed(markdown) {
      if (!this.quill) return;
      // setContents, e nao dangerouslyPasteHTML: este ainda chama
      // setSelection(0), que da foco ao editor e o roubaria das datas e
      // checkboxes da Liberacao a cada re-semeadura.
      this.quill.setContents(this.quill.clipboard.convert(toEditorHtml(markdown)), 'silent');
      // O v-model do vue2-editor precisa bater com o DOM, senao o watcher dele
      // re-semeia por innerHTML e o problema volta.
      this.html = this.quill.root.innerHTML;
      this.seededHtml = this.html;
      this.seededMarkdown = markdown || '';
      // Sem isto, Ctrl+Z desfaz ate ANTES da semeadura programatica (ex.: o
      // texto que a Liberacao gerou), passando por estados que a pessoa nunca
      // digitou.
      this.quill.history.clear();
    },
    /** Insere no cursor (ou no fim) como edicao DA PESSOA: a Liberacao congela o texto. */
    insertAtCursor(token) {
      if (!this.quill) return;
      const range = this.quill.getSelection() || this.lastRange;
      const index = range ? range.index : Math.max(0, this.quill.getLength() - 1);
      this.quill.insertText(index, token, 'user');
      this.quill.setSelection(index + token.length, 0, 'silent');
    },
  },
  watch: {
    html(value) {
      // De volta ao HTML semeado (ao carregar, ou Ctrl+Z ate ele): vale o
      // Markdown original, nao a conversao dele.
      const markdown = value === this.seededHtml ? this.seededMarkdown : htmlToMarkdown(value);
      if (markdown === this.lastEmitted) return;
      this.lastEmitted = markdown;
      this.$emit('input', markdown);
    },
    /**
     * So re-semeia o editor quando o Markdown veio de FORA (carregar campanha
     * existente, reset do formulario). Quando veio da propria digitacao, bate
     * com `lastEmitted` e o Quill fica intocado — e o cursor tambem.
     */
    value(next) {
      if ((next || '') === this.lastEmitted) return;
      this.lastEmitted = next || '';
      this.seed(next);
    },
  },
};
</script>

<style scoped>
/* A barra do Quill herda cor e largura do CSS global do Master, que estica
   botao dentro de .default-box — era o que sumia com os icones e espalhava os
   botoes pela largura toda. `>>>` atravessa o scoped ate o markup do Quill. */
.broadcast-editor >>> .ql-toolbar.ql-snow {
  border-color: #d5d7da;
  border-radius: 6px 6px 0 0;
  background: #fafafa;
  text-align: left;
}

.broadcast-editor >>> .ql-toolbar.ql-snow button {
  flex: 0 0 auto;
  width: 28px;
  height: 24px;
  padding: 0 3px;
  margin: 0;
  background: transparent;
}

.broadcast-editor >>> .ql-toolbar.ql-snow .ql-stroke {
  stroke: #3a424e;
}

.broadcast-editor >>> .ql-toolbar.ql-snow .ql-fill {
  fill: #3a424e;
}

.broadcast-editor >>> .ql-toolbar.ql-snow .ql-picker-label {
  color: #3a424e;
}

.broadcast-editor >>> .ql-container.ql-snow {
  border-color: #d5d7da;
  border-radius: 0 0 6px 6px;
  font-family: inherit;
  font-size: 14px;
}

.broadcast-editor >>> .ql-editor {
  min-height: 200px;
}

.broadcast-editor__footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}

.broadcast-editor__counter {
  font-size: 12px;
  color: #4e5560;
}

.broadcast-editor__counter.is-over {
  color: #ff445d;
  font-weight: 600;
}

.broadcast-editor__hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #4e5560;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/broadcast/broadcastEditor.vue